import { ComponentType, createElement } from 'react'
import { RouteObject } from 'react-router'
import { announceRoute } from '../../NonReact/common/RxJS/RouterState'
import { ChildRouteID, RouteID } from '../Components/Molecules/Header/Types/Types'
import { createRouteURI } from '../Utilities/RoutingUtils'
import { EMPTY_STRING } from '../../NonReact/constants/Common'

interface RouteOptions {
    children?: ChildRouteID[]
}

/**
 * Generates a unique route ID for a child route based on its parent route ID and its own child ID.
 *
 * @param parentID - The ID of the parent route, which will be used as a prefix for the child route ID.
 * @param childID - The ID of the child route, which will be appended to the parent route ID to create a unique child route ID.
 */
export const generateChildRouteID = (parentID: RouteID, childID: ChildRouteID): string =>
    `${parentID ?? EMPTY_STRING}-${childID ?? EMPTY_STRING}`

/**
 * Decorator for React components that should be rendered as a route.
 * This will announce the route to the application, which will allow it to be rendered when navigated to.
 * If children are provided, they will also be announced as routes with the same component, but with their own paths.
 * This is to allow for nested routes to be rendered with the same component, without having to declare the same component multiple times.
 *
 * @param id - The ID of the route, which will be used to create the path and identify the route in the application.
 * @param children - An optional array of child route IDs, which will be announced as routes with the same component but their own paths.
 *
 * @return A decorator function that takes a React component and announces it as a route with the specified ID and children.
 */
export function Route(id: RouteID, { children }: RouteOptions = {}) {
    return (target: ComponentType): void => {
        announceRoute({
            path: `${createRouteURI(id)}`,
            id,
            element: createElement(target),
            children: children?.map(
                (child: ChildRouteID): RouteObject => ({
                    id: generateChildRouteID(id, child),
                    path: `${createRouteURI(id, child)}`,
                    element: createElement(target)
                })
            )
        })
    }
}
